import { computed, shallowRef, type Ref } from 'vue';
import {
  VIETNAM_ISLAND_MARKERS,
  type VietnamIslandMarkerDef,
} from '@/components/dashboard/map/constants/vietnam-island-markers';
import { CAP_DON_VI } from '@/core/constants/dashboard-map.constants';

export interface VietnamIslandScreenPosition {
  id: string;
  x: number;
  y: number;
}

export interface VietnamIslandProjectedMarker {
  marker: VietnamIslandMarkerDef;
  position: VietnamIslandScreenPosition;
}

interface ProjectableMap {
  project: (lngLat: [number, number]) => { x: number; y: number };
}

/**
 * Independent HTML overlay for Hoàng Sa / Trường Sa.
 * Visible for company (cap_don_vi = 1) and unit (cap_don_vi = 2) dashboards.
 * Stays projected on zoom-out so markers remain when the East Sea enters view.
 * Does not participate in warehouse markers, clustering, or callout collision.
 */
export function useVietnamIslandMarkers(options: {
  capDonVi: Ref<number | undefined>;
}) {
  const screenPositions = shallowRef<VietnamIslandProjectedMarker[]>([]);

  const isVisible = computed(() => {
    const cap = options.capDonVi.value;
    return cap === CAP_DON_VI.CONG_TY || cap === CAP_DON_VI.DON_VI;
  });

  function projectIslandMarkers(map: ProjectableMap | null): void {
    if (!map || !isVisible.value) {
      screenPositions.value = [];
      return;
    }

    screenPositions.value = VIETNAM_ISLAND_MARKERS.map((marker) => {
      const point = map.project([marker.longitude, marker.latitude]);
      return {
        marker,
        position: {
          id: marker.id,
          x: point.x,
          y: point.y,
        },
      };
    }).filter(
      (item) =>
        Number.isFinite(item.position.x) && Number.isFinite(item.position.y)
    );
  }

  function clearIslandMarkers(): void {
    screenPositions.value = [];
  }

  return {
    islandMarkers: VIETNAM_ISLAND_MARKERS,
    islandScreenPositions: screenPositions,
    isIslandOverlayVisible: isVisible,
    projectIslandMarkers,
    clearIslandMarkers,
  };
}
